import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../lib/constants";
import { useTranslation } from "../lib/i18n";
import LogoMark from "./LogoMark";
import LanguageSwitcher from "./LanguageSwitcher";
import QuoteBranchModal from "./QuoteBranchModal";

const NAV_KEY = {
  "/": "nav.home",
  "/services": "nav.services",
  "/destinations": "nav.destinations",
  "/corporate-solutions": "nav.corporate",
  "/about": "nav.about",
  "/contact": "nav.contact",
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#05070C]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <LogoMark />

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
            >
              <span className="relative">
                {t(NAV_KEY[link.to] || link.label)}
                <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100" />
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setQuoteOpen(true)}
            data-testid="navbar-quote-cta"
            className="btn-gold group"
          >
            {t("nav.getQuote")}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher compact />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-white/90 p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="container-x pb-8 pt-2 glass-strong border-t border-white/5">
          <ul className="flex flex-col">
            {NAV_LINKS.map((link) => (
              <li key={link.to} className="py-3 border-b border-white/5">
                <NavLink
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between text-base ${isActive ? "text-gold" : "text-white/85"}`
                  }
                >
                  <span>{t(NAV_KEY[link.to] || link.label)}</span>
                  <ArrowUpRight className="h-4 w-4 opacity-60" />
                </NavLink>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setQuoteOpen(true);
            }}
            data-testid="mobile-quote-cta"
            className="btn-gold w-full mt-6"
          >
            {t("nav.getQuote")}
          </button>
        </div>
      </div>

      <QuoteBranchModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </header>
  );
};

export default Navbar;
